import apiClient from './api';

export type DashboardAlertSeverity = 'error' | 'warning' | 'info';
export type DashboardAlertPriority = 'critical' | 'high' | 'normal' | 'low';
export type DashboardAlertIconKey =
  | 'return'
  | 'handover'
  | 'payment'
  | 'document'
  | 'lease'
  | 'license'
  | 'contract'
  | 'wrench'
  | 'recurring'
  | 'drawer';
export type DashboardAlertType =
  | 'overdue_return'
  | 'missed_handover'
  | 'unpaid_balance'
  | 'post_return_balance'
  | 'contract_unsigned'
  | 'client_license_expiring'
  | 'document_expiry'
  | 'vehicle_maintenance_due'
  | 'lease_payment_missed'
  | 'monthly_fixed_costs_due'
  | 'recurring_income_due'
  | 'cash_drawer_unreconciled';
export type DashboardAlertEventType =
  | 'created'
  | 'updated'
  | 'dismissed'
  | 'undismissed'
  | 'snoozed'
  | 'unsnoozed'
  | 'assigned'
  | 'unassigned';

export interface DashboardAlertAssignee {
  userId: string | null;
  name: string | null;
}

export interface DashboardAlertEvent {
  type: DashboardAlertEventType;
  at: string;
  byUserId: string | null;
  byName: string | null;
  meta?: Record<string, unknown>;
}

export interface DashboardAlert {
  _id: string;
  agencyId: string;
  type: DashboardAlertType;
  severity: DashboardAlertSeverity;
  priority: DashboardAlertPriority;
  title: string;
  subtitle: string;
  meta: string;
  href: string;
  iconKey: DashboardAlertIconKey;
  source: {
    kind: 'booking' | 'vehicle' | 'expense' | 'income' | 'cash_drawer';
    refId: string;
    subKey?: string;
  };
  snapshot?: Record<string, unknown>;
  dismissed: boolean;
  dismissedAt?: string | null;
  dismissedByName?: string | null;
  snoozeUntil?: string | null;
  snoozedAt?: string | null;
  snoozedByName?: string | null;
  assignedTo?: DashboardAlertAssignee;
  assignedAt?: string | null;
  assignedByName?: string | null;
  events?: DashboardAlertEvent[];
  groupKey?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ListAlertsResponse {
  success: boolean;
  data: { alerts: DashboardAlert[]; count: number };
}

interface AlertActionResponse {
  success: boolean;
  data: DashboardAlert;
}

interface SyncResponse {
  success: boolean;
  data: { booked: number; vehicles: number };
}

const BASE = '/agency/alerts';
let alertListPromise: Promise<DashboardAlert[]> | null = null;
let alertListCache: DashboardAlert[] | null = null;
let alertListLastFetchedAt = 0;
const ALERT_LIST_MIN_REFETCH_MS = 10_000;

const alertService = {
  async list(options?: { includeDismissed?: boolean; onlyDismissed?: boolean; sync?: boolean; limit?: number }): Promise<DashboardAlert[]> {
    const now = Date.now();
    // Only the default "active" query is cached. Dismissed-tab reads always
    // hit the server so restoring/re-dismissing reflects immediately.
    const cacheable = !options?.includeDismissed && !options?.onlyDismissed;
    if (cacheable && alertListPromise) {
      return alertListPromise;
    }
    if (cacheable && alertListCache && now - alertListLastFetchedAt < ALERT_LIST_MIN_REFETCH_MS) {
      return alertListCache;
    }

    const params: Record<string, string> = {};
    if (options?.includeDismissed) params.includeDismissed = 'true';
    if (options?.onlyDismissed) params.onlyDismissed = 'true';
    if (options?.sync) params.sync = 'true';
    if (options?.limit) params.limit = String(options.limit);

    const request = (async () => {
      try {
        const response = await apiClient.get<ListAlertsResponse>(BASE, { params });
        if (cacheable) {
          alertListCache = response.data.data.alerts;
          alertListLastFetchedAt = Date.now();
        }
        return response.data.data.alerts;
      } finally {
        if (cacheable) {
          alertListPromise = null;
        }
      }
    })();

    if (cacheable) {
      alertListPromise = request;
    }

    return request;
  },

  async sync(): Promise<{ booked: number; vehicles: number }> {
    const response = await apiClient.post<SyncResponse>(`${BASE}/sync`);
    return response.data.data;
  },

  async dismiss(alertId: string): Promise<DashboardAlert> {
    const response = await apiClient.post<AlertActionResponse>(`${BASE}/${alertId}/dismiss`);
    alertListCache = null;
    return response.data.data;
  },

  async undismiss(alertId: string): Promise<DashboardAlert> {
    const response = await apiClient.post<AlertActionResponse>(`${BASE}/${alertId}/undismiss`);
    alertListCache = null;
    return response.data.data;
  },

  async snooze(alertId: string, until: Date | string): Promise<DashboardAlert> {
    const untilIso = typeof until === 'string' ? until : until.toISOString();
    const response = await apiClient.post<AlertActionResponse>(`${BASE}/${alertId}/snooze`, { until: untilIso });
    alertListCache = null;
    return response.data.data;
  },

  async unsnooze(alertId: string): Promise<DashboardAlert> {
    const response = await apiClient.post<AlertActionResponse>(`${BASE}/${alertId}/unsnooze`);
    alertListCache = null;
    return response.data.data;
  },

  async assign(alertId: string, assignee: { userId: string; name?: string } | null): Promise<DashboardAlert> {
    const payload = assignee ? { userId: assignee.userId, name: assignee.name } : {};
    const response = await apiClient.post<AlertActionResponse>(`${BASE}/${alertId}/assign`, payload);
    alertListCache = null;
    return response.data.data;
  },
};

export default alertService;
